/**
 * Pure decision logic for the streaming audio pipeline.
 *
 * Extracted from use-audio-processor.ts so it can be unit-tested without pulling
 * in the AudioContext / AudioWorklet Web APIs (unavailable in jsdom) or the
 * `import.meta`-dependent logging chain.
 */

/**
 * What setupAudioPipeline should do with the current audio graph.
 *
 * - `build`          — no usable context exists; construct the whole graph
 *                      (AudioContext + worklet + analyser + source).
 * - `rebuild-source` — the context + worklet are alive (e.g. across a
 *                      pause→resume cycle) but the mic source is stale; recreate
 *                      only the MediaStreamAudioSourceNode on the existing context.
 * - `reuse`          — everything is alive and valid; no node changes needed.
 */
export type PipelineAction = "build" | "rebuild-source" | "reuse";

export interface PipelineState {
  /** An AudioContext ref is present. */
  hasContext: boolean;
  /** The present AudioContext is in the "closed" state (unusable). */
  isContextClosed: boolean;
  /** An AudioWorkletNode ref is present. */
  hasProcessor: boolean;
  /** A MediaStreamAudioSourceNode ref is present. */
  hasSource: boolean;
  /** The caller obtained a fresh mic stream, so the old source is stale. */
  needsNewSource: boolean;
}

/**
 * Decide how to (re)establish the audio pipeline.
 *
 * Reusing a single long-lived AudioContext across pause→resume keeps the worklet's
 * resample ratio stable (one context = one sample rate) and avoids leaking contexts,
 * which is the root cause of both the "no transcription after N resumes" and the
 * "later sessions distorted" bugs.
 */
export const decidePipelineAction = (state: PipelineState): PipelineAction => {
  if (!state.hasContext || state.isContextClosed || !state.hasProcessor) {
    return "build";
  }
  if (state.needsNewSource || !state.hasSource) {
    return "rebuild-source";
  }
  return "reuse";
};
